import React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

// A small presentational component that renders wishes as a postcard to Santa
// Props:
//  - wishes: string (required)
//  - receiverName: string | undefined (optional, used in header)
export default function WishesPostcard({
                                         wishes,
                                         from,
                                         // editable mode props
                                         editable = false,
                                         value,
                                         onChange,
                                         disabled = false,
                                       }) {
  const theme = useTheme();

  let wishesPlaceholder =
    `Dear Santa, this year I was very good/bad boy/girl, but I definitely promise that next year I would be better/worse!

I would be really glad to receive ...

Thank you very much dear Santa, merry whatever you celebrating and have a great new year!`;

  return (
    <Paper sx={{
      position: 'relative',
      p: 3,
      backgroundColor: '#fffdf7', // Authentic postcard color
      border: `2px solid ${theme.palette.error.main}`,
      borderRadius: 2,
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
      overflow: 'hidden',
    }} variant="outlined">
      <Box sx={{
        position: 'absolute',
        top: 1.5,
        right: 1.5,
        width: 56,
        height: 56,
        backgroundImage: 'url(/santa-claus.png)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        border: '2px dashed rgba(0,0,0,0.15)',
        borderRadius: 1.5,
        opacity: 0.9,
      }} />
      <Box sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `repeating-linear-gradient(
          to bottom,
          transparent 0px,
          transparent 28px,
          rgba(0,0,0,0.06) 29px
        )`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute',
        top: 13,
        right: 1.5,
        width: 130,
        textAlign: 'right',
        zIndex: 1,
        pointerEvents: 'none',
        border: '2px dashed rgba(0,0,0,0.15)',
        borderRadius: 1.5,
        opacity: 0.9,
      }} aria-hidden="true">
        <Typography variant="body2" sx={{ lineHeight: '22px', color: theme.palette.error.dark, fontFamily: 'inherit', pl: 0.625, pr: 1.25 }}>TO: Santa Claus</Typography>
        <Typography variant="body2" sx={{ lineHeight: '22px', color: theme.palette.error.dark, fontFamily: 'inherit', pl: 0.625, pr: 1.25 }}>North Pole</Typography>
        <Typography variant="body2" sx={{ lineHeight: '22px', color: theme.palette.error.dark, fontFamily: 'inherit', pl: 0.625, pr: 1.25 }}>Arctic Circle</Typography>
        <Typography variant="body2" sx={{ lineHeight: '22px', color: theme.palette.error.dark, fontFamily: 'inherit', pl: 0.625, pr: 1.25 }}>Earth</Typography>
      </Box>
      <Box sx={{
        position: 'relative',
        zIndex: 1,
        color: 'text.primary',
        whiteSpace: 'pre-line',
      }}>
        <Typography variant="subtitle1" sx={{
          color: theme.palette.error.dark,
          fontWeight: 600,
          mb: 1,
        }}>
          Letter to Santa{from?.name ? ` from ${from?.name}` : ''}
        </Typography>
        {editable ? (
          <textarea
            aria-label="Write your wishes to Santa"
            placeholder={wishesPlaceholder}
            disabled={disabled}
            value={value ?? ''}
            onChange={onChange}
            style={{
              width: '80%',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'vertical',
              color: 'inherit',
              fontFamily: 'inherit',
              fontSize: '1rem',
              lineHeight: '29px',
              minHeight: 4 * 29,
              paddingRight: 240,
              wordBreak: 'break-word',
              wordWrap: 'break-word',
              overflowWrap: 'anywhere',
            }}
            rows={8}
          />
        ) : (
          <Typography sx={{
            width: '80%',
            fontSize: '1rem',
            lineHeight: '29px',
            minHeight: 4 * 29,
            paddingRight: 240,
            wordBreak: 'break-word',
            wordWrap: 'break-word',
            overflowWrap: 'anywhere',
          }}>{wishes || '— No wishes provided —'}</Typography>
        )}
        <Typography variant="caption" sx={{
          mt: 2,
          color: 'text.secondary',
          fontStyle: 'italic',
        }}>
          Ho-ho-ho! Make it joyful and kind.
        </Typography>
      </Box>
    </Paper>
  );
}
